import { storeToRefs } from 'pinia';
import { useViewerTemplateStore, type Template } from '../store/useViewerTemplateStore';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useUserStore } from '@/store/useUserStore';
import { useToast, TOAST_TYPE } from '@/composables/useToast';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { useTemplateConfigModal } from './useTemplateConfigModal';
import { closeTemplatesModal } from './useTemplatesModal';

export const useTemplateManager = () => {
  const templateStore = useViewerTemplateStore();
  const itemStore = useViewerItemStore();
  const userStore = useUserStore();
  const { toast } = useToast();
  const { debug } = useDebug(DEBUG_NAMESPACE.VIEWER_TEMPLATE);
  const { showTemplateConfigModal } = useTemplateConfigModal();

  const { id: currentUserId } = storeToRefs(userStore);
  const { id: currentItemId } = storeToRefs(itemStore);

  // Helper functions
  const showToast = (message: string, type = TOAST_TYPE.SUCCESS) => toast(message, type);
  const showError = (message: string) => showToast(message, TOAST_TYPE.ERROR);

  const validateItemSelected = () => {
    if (!currentItemId.value) {
      showError('Unable to apply template: no item selected');
      return false;
    }
    return true;
  };

  const handleConfigure = async (slot?: string) => {
    if (slot) {
      debug(`[handleConfigure] Configuring template slot: ${slot}`);
      showTemplateConfigModal(slot);
    } else {
      showTemplateConfigModal();
    }
  };

  const handleApplyTemplate = async (slot: string) => {
    debug(`[handleApplyTemplate] Applying ${slot} template`);

    const template = templateStore.getTemplateBySlot(slot);

    if (template) {
      // Template exists, apply it
      if (!validateItemSelected()) return;

      try {
        await templateStore.applyTemplate(template.id, currentItemId.value!);
        showToast('Template applied successfully');
        closeTemplatesModal();
      } catch (error) {
        debug(`[handleApplyTemplate] Error applying template ${slot}:`, error);
        showError('Error applying template');
      }
    } else {
      // Template not configured yet, open configuration
      showTemplateConfigModal(slot);
    }
  };

  const handleCreateNew = async () => {
    debug('[handleCreateNew] Creating new custom template');

    // Open sidebar modal for new template creation
    showTemplateConfigModal();
  };

  const handleToggleDefault = async (template: Template) => {
    debug(`[handleToggleDefault] Toggling default for template slot ${template.slot}`);

    try {
      const action = template.isDefault ? 'removed as' : 'set as';
      await templateStore.updateDefaultTemplate(template);
      showToast(`Template ${template.name} ${action} default`);
    } catch (error) {
      debug('[handleToggleDefault] Error toggling default template:', error);
      showError('Error updating default template');
    }
  };

  const isTemplateOwner = (slot: string) => {
    const template = templateStore.getTemplateBySlot(slot);
    return template && currentUserId && currentUserId.value ? templateStore.isTemplateOwner(template, currentUserId.value) : false;
  };

  const isTemplateConfigured = (slot: string) => templateStore.isTemplateConfigured(slot);

  const getButtonText = (slot: string) => (isTemplateConfigured(slot) ? 'Apply' : 'Configure');
  const getButtonVariant = (slot: string) => (isTemplateConfigured(slot) ? 'gray' : 'black');
  const getButtonIcon = (slot: string) => (isTemplateConfigured(slot) ? 'general.bolt' : 'general.cog');

  return {
    // Template actions
    handleApplyTemplate,
    handleConfigure,
    handleCreateNew,
    handleToggleDefault,

    // Utility functions
    isTemplateOwner,
    isTemplateConfigured,
    getButtonText,
    getButtonVariant,
    getButtonIcon,
  };
};
